import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Select,
    IconButton,
    Typography,
    Autocomplete,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
    Activity,
    ActivityCategory,
    Technology,
    Employee,
    CreateActivityDto,
    UpdateActivityDto,
} from '../../types';

interface ActivityModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (activity: CreateActivityDto | UpdateActivityDto) => Promise<void>;
    activity?: Activity | null;
    activities: Activity[];
    categories: ActivityCategory[];
    technologies: Technology[];
    employees: Employee[];
    headingOverride?: string;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
    open,
    onClose,
    onSave,
    activity,
    activities,
    categories,
    technologies,
    employees,
    headingOverride,
}) => {
    const [formData, setFormData] = useState({
        id: 0,
        categoryId: 0,
        technologyId: 0,
        name: '',
        description: '',
        pointOfContactId: null as number | null,
        status: '',
    });

    const [categoryInput, setCategoryInput] = useState('');
    const [technologyInput, setTechnologyInput] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isExistingActivity, setIsExistingActivity] = useState(false);
    const isAddMode = !activity;

    useEffect(() => {
        if (activity) {
            setFormData({
                id: activity.id,
                categoryId: activity.categoryId,
                technologyId: activity.technologyId,
                name: activity.name,
                description: activity.description,
                pointOfContactId: activity.pointOfContactId,
                status: activity.status,
            });
            setCategoryInput(activity.categoryName ?? '');
            setTechnologyInput(activity.technologyName ?? '');
        } else {
            setFormData({
                id: 0,
                categoryId: 0,
                technologyId: 0,
                name: '',
                description: '',
                pointOfContactId: null,
                status: '',
            });
            setCategoryInput('');
            setTechnologyInput('');
        }
        setErrors({});
        setIsExistingActivity(false);
    }, [activity, open]);

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.categoryId) newErrors.categoryId = 'Category is required';
        if (!formData.technologyId) newErrors.technologyId = 'Technology is required';
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        } else if (formData.name.length > 80) {
            newErrors.name = 'Please limit to 80 characters';
        } else if (!/[a-zA-Z]/.test(formData.name)) {
            newErrors.name = 'Activity name cannot be only numbers or special characters';
        } else {
            const duplicate = activities.some(
                a =>
                    a.name.toLowerCase().trim() === formData.name.toLowerCase().trim() &&
                    String(a.id) !== String(activity?.id)
            );
            if (duplicate) newErrors.name = 'Activity name already exists';
        }
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (!formData.pointOfContactId) newErrors.pointOfContactId = 'Point of Contact is required';
        if (!formData.status) newErrors.status = 'Status is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        if (!/[a-zA-Z]/.test(formData.name)) {
            setErrors(prev => ({ ...prev, name: 'Activity name cannot be only numbers or special characters' }));
            return;
        }
        if (!validate()) return;
        try {
            setIsSubmitting(true);
            const payload = {
                ...formData,
                categoryId: formData.categoryId ?? 0,
                technologyId: formData.technologyId ?? 0,
                pointOfContactId: formData.pointOfContactId ?? 0,
            };
            if (activity) {
                await onSave({ ...payload, id: activity.id } as UpdateActivityDto);
            } else {
                const { id, ...createPayload } = payload;
                await onSave(createPayload as CreateActivityDto);
            }
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ── Compact inline styles ── */
    const labelStyle: React.CSSProperties = {
        fontSize: '12px',
        fontWeight: 500,
        color: '#1a1a1a',
        marginBottom: '4px',
        display: 'block',
    };
    const requiredStar: React.CSSProperties = { color: '#d32f2f', marginLeft: '2px' };
    const fieldWrap: React.CSSProperties = { marginBottom: '10px' };

    // Standard input style
    const inputStyle = {
        borderRadius: '6px',
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d5dd' },
        '& .MuiInputBase-root': { fontSize: '12px' },
        '& .MuiInputBase-input': { padding: '6px 8px' },
    };

    // Status select — fixed compact height
    const statusStyle = {
        borderRadius: '6px',
        backgroundColor: '#fff',
        fontSize: '12px',
        height: '34px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d5dd' },
        '& .MuiSelect-select': {
            padding: '6px 8px !important',
            fontSize: '12px',
            lineHeight: '1.4',
            display: 'flex',
            alignItems: 'center',
        },
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose();
            }}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px', padding: '0', maxHeight: '90vh' },
            }}
        >
            {/* ── Header ── */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    py: 1.5,
                    px: 3,
                }}
            >
                <Typography fontWeight={700} fontSize={16} sx={{ color: '#124B84' }}>
                    {headingOverride || (activity ? 'Edit Activity' : 'Add Activity')}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: '#124B84' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* ── Form ── */}
            <DialogContent sx={{ px: 3, pt: 2, pb: 0 }}>

                {/* 1. Name */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Name<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        freeSolo
                        options={Array.from(
                            new Map((activities || []).map((a: any) => [a.id ?? a.name, a])).values()
                        )
                            .slice()
                            .sort((x: any, y: any) =>
                                String(x.name || x.activityName || '').toLowerCase().localeCompare(
                                    String(y.name || y.activityName || '').toLowerCase()
                                )
                            )}
                        getOptionLabel={(option: any) =>
                            typeof option === 'string' ? option : option?.name ?? option?.activityName ?? ''
                        }
                        value={
                            isExistingActivity
                                ? (activities || []).find(a => a.id === formData.id) || null
                                : formData.name
                                    ? { name: formData.name }
                                    : null
                        }
                        onChange={(_, value) => {
                            if (!value) {
                                setIsExistingActivity(false);
                                handleChange('name', '');
                                handleChange('id', 0);
                                if (isAddMode) {
                                    handleChange('categoryId', 0);
                                    handleChange('technologyId', 0);
                                    handleChange('description', '');
                                    handleChange('pointOfContactId', null);
                                    handleChange('status', '');
                                }
                                return;
                            }
                            if (typeof value === 'string') {
                                setIsExistingActivity(false);
                                handleChange('name', value);
                                handleChange('id', 0);
                                return;
                            }
                            const selected = value as any;
                            setIsExistingActivity(true);
                            handleChange('id', selected.id ?? selected.activityId ?? 0);
                            handleChange('name', selected.name ?? selected.activityName ?? '');
                            handleChange('categoryId', selected.categoryId ?? 0);
                            handleChange('technologyId', selected.technologyId ?? 0);
                            handleChange('description', selected.description ?? selected.activityDescription ?? '');
                            handleChange('pointOfContactId', selected.pointOfContactId ?? null);
                            handleChange('status', selected.status ?? '');
                        }}
                        inputValue={formData.name}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input') {
                                setIsExistingActivity(false);
                                handleChange('name', value);
                            }
                        }}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Activity Name"
                                error={Boolean(errors.name)}
                                helperText={errors.name || `${formData.name.length}/50 characters`}
                                inputProps={{ ...params.inputProps, maxLength: 50 }}
                                sx={inputStyle}
                            />
                        )}
                    />
                </div>

                {/* 2. Category */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Category<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={categories
                            .filter((c: any) => String(c.status || '').toLowerCase() === 'active')
                            .slice()
                            .sort((a: any, b: any) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))}
                        value={categories.find(c => c.id === formData.categoryId) ?? null}
                        inputValue={categoryInput}
                        onInputChange={(_, newInput) => setCategoryInput(newInput)}
                        getOptionLabel={(option) => option?.name ?? ''}
                        isOptionEqualToValue={(option, value) => option.id === (value as any)?.id}
                        onChange={(_, value) => {
                            if (isExistingActivity && isAddMode) return;
                            setCategoryInput(value ? (value as any).name : '');
                            handleChange('categoryId', value ? (value as any).id : 0);
                        }}
                        disabled={isExistingActivity && isAddMode}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={Boolean(errors.categoryId)}
                                helperText={errors.categoryId}
                                sx={inputStyle}
                            />
                        )}
                    />
                </div>

                {/* 3. Technology */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Technology<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={technologies
                            .filter((t: any) => String(t.status || '').toLowerCase() === 'active')
                            .slice()
                            .sort((a: any, b: any) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))}
                        value={formData.technologyId ? technologies.find(t => t.id === formData.technologyId) ?? null : null}
                        inputValue={technologyInput}
                        onInputChange={(_, newInput) => setTechnologyInput(newInput)}
                        getOptionLabel={(option) => option?.name ?? ''}
                        isOptionEqualToValue={(option, value) => option.id === (value as any)?.id}
                        onChange={(_, value) => {
                            if (isExistingActivity && isAddMode) return;
                            setTechnologyInput(value ? (value as any).name : '');
                            handleChange('technologyId', value ? (value as any).id : 0);
                        }}
                        disabled={isExistingActivity && isAddMode}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={Boolean(errors.technologyId)}
                                helperText={errors.technologyId}
                                sx={inputStyle}
                            />
                        )}
                    />
                </div>

                {/* 4. Description */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Description<span style={requiredStar}>*</span>
                    </label>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Write here..."
                        value={formData.description}
                        onChange={e => {
                            if (e.target.value.length <= 300) handleChange('description', e.target.value);
                        }}
                        error={Boolean(errors.description)}
                        helperText={
                            errors.description
                                ? errors.description
                                : `${formData.description.length}/300 characters`
                        }
                        inputProps={{ maxLength: 300 }}
                        disabled={isExistingActivity && isAddMode}
                        size="small"
                        sx={inputStyle}
                    />
                </div>

                {/* 5. Point of Contact */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Point of Contact<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={employees.slice().sort((a: any, b: any) => {
                            const na = (a.displayName || a.name || a.empName || '').toString().toLowerCase();
                            const nb = (b.displayName || b.name || b.empName || '').toString().toLowerCase();
                            return na.localeCompare(nb);
                        })}
                        value={
                            formData.pointOfContactId
                                ? employees.find(e => e.id === formData.pointOfContactId) ?? null
                                : null
                        }
                        getOptionLabel={option => option?.displayName ?? ''}
                        isOptionEqualToValue={(option, value) => option.id === (value as any)?.id}
                        onChange={(_, value) => {
                            if (isExistingActivity && isAddMode) return;
                            handleChange('pointOfContactId', value ? value.id : null);
                        }}
                        disabled={isExistingActivity && isAddMode}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={Boolean(errors.pointOfContactId)}
                                helperText={errors.pointOfContactId}
                                sx={inputStyle}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {params.InputProps.endAdornment}
                                            <InputAdornment position="end">
                                                <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </div>

                {/* 6. Status */}
                <div style={{ ...fieldWrap, marginBottom: 0 }}>
                    <label style={labelStyle}>
                        Status<span style={requiredStar}>*</span>
                    </label>
                    <Select
                        fullWidth
                        displayEmpty
                        value={formData.status}
                        onChange={e => handleChange('status', e.target.value)}
                        disabled={isExistingActivity && isAddMode}
                        size="small"
                        error={Boolean(errors.status)}
                        sx={statusStyle}
                        renderValue={(val) => val || <span style={{ color: '#9e9e9e', fontSize: '12px' }}>Select</span>}
                    >
                        <MenuItem value="Active" sx={{ fontSize: '12px' }}>Active</MenuItem>
                        <MenuItem value="Inactive" sx={{ fontSize: '12px' }}>Inactive</MenuItem>
                    </Select>
                    {errors.status && (
                        <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>{errors.status}</div>
                    )}
                </div>

            </DialogContent>

            {/* ── Footer Buttons ── */}
            <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                        backgroundColor: '#124B84',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 3,
                        '&:hover': { backgroundColor: '#0f3f70' },
                        '&:disabled': { backgroundColor: '#bdbdbd' },
                    }}
                >
                    {activity ? 'Update' : 'Create'}
                </Button>
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        backgroundColor: '#e53935',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 3,
                        '&:hover': { backgroundColor: '#c62828' },
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ActivityModal;